import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Mail, Zap } from 'lucide-react';
import { EventProLogo } from '../components/ui/Logo';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  // États pour le logo client
  const [companyName, setCompanyName] = useState("Chargement...");
  const [companyLogo, setCompanyLogo] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/settings/public-config`)
        .then(res => res.json())
        .then(data => {
            setCompanyName(data.company_name);
            setCompanyLogo(data.logo_base64);
        })
        .catch(() => setCompanyName(""));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error('Erreur serveur');
      setSent(true);
      toast.success('Email envoyé si le compte existe');
    } catch {
      toast.error('Erreur lors de la demande');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center p-4 bg-slate-100 font-sans">
      
      {/* LOGO EVENTPRO - EN HAUT À GAUCHE */}
      <div className="absolute top-6 left-6 md:top-8 md:left-8">
        <div className="bg-sky-500/50 backdrop-blur px-4 py-2 rounded-xl shadow-sm flex items-center justify-center border border-slate-200">
            <EventProLogo className="scale-90 origin-left" />
        </div>
      </div>

      <div className="w-full max-w-md space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700 mt-16 md:mt-0">
        
        {/* LOGO DU CLIENT - CENTRÉ */}
        <div className="flex flex-col items-center justify-center mb-6">
            {companyLogo ? (
                <img src={companyLogo} alt="Logo Client" className="h-20 w-auto object-contain mix-blend-multiply drop-shadow-sm mb-2" />
            ) : (
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm border border-slate-200 mb-2 text-primary">
                    <Zap className="w-8 h-8" />
                </div>
            )}
            
            {companyName !== "Chargement..." && (
                <h1 className="text-2xl font-bold text-slate-900">{companyName}</h1>
            )}
            <p className="text-slate-500 font-bold tracking-widest uppercase text-xs mt-2">Récupération d'accès</p>
        </div>

        <Card className="border-none shadow-xl bg-white rounded-2xl overflow-hidden">
          <CardContent className="p-8">
            <div className="mb-6 text-center">
                <h2 className="text-2xl font-bold text-slate-900">Mot de passe oublié</h2>
                <p className="text-sm text-slate-500 mt-2">
                    {sent 
                        ? "Consultez votre boîte mail." 
                        : "Saisissez votre email pour recevoir un lien de réinitialisation."}
                </p>
            </div>

            {sent ? (
              <div className="text-center space-y-6 animate-in zoom-in duration-300">
                <div className="w-20 h-20 mx-auto rounded-full bg-blue-50 flex items-center justify-center border border-blue-100 shadow-inner">
                  <Mail className="w-10 h-10 text-blue-500" />
                </div>
                <p className="text-sm text-slate-600 bg-slate-50 p-4 rounded-xl border border-slate-100 leading-relaxed">
                  Si un compte est lié à l'adresse <strong>{email}</strong>, nous vous avons envoyé les instructions de réinitialisation (lien valide 15 min).
                </p>
                <Button variant="outline" className="w-full h-12 text-slate-700 border-slate-200 hover:bg-slate-50" onClick={() => setSent(false)}>
                  Renvoyer l'email
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium">Adresse email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="vous@entreprise.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email"
                    className="bg-slate-50 focus:bg-white border-slate-200 h-12"
                  />
                </div>
                <Button type="submit" className="w-full h-14 text-lg font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5" disabled={loading} data-testid="forgot-submit">
                  {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
                  Envoyer le lien
                </Button>
              </form>
            )}

            <div className="mt-8 text-center border-t border-slate-100 pt-6">
              <Link to="/login" className="text-sm font-medium text-slate-500 hover:text-slate-900 inline-flex items-center justify-center gap-2 transition-colors">
                <div className="bg-slate-100 p-1.5 rounded-md"><ArrowLeft className="w-4 h-4" /></div> Retour à la connexion
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;